import { ApiUrlPrepare } from "@lib/helpers/ApiHelper";
import { API } from "@lib/services/http";
import { ApiParams, PaginationType } from "@lib/types/Api";
import {
  ManualTransaction,
  TransactionAttachment,
  TransactionType,
  TransactionTypeLog,
} from "@lib/types/Transaction";
import redux from "@lib/redux";
import { BE_BASE_URL } from "@lib/constants/AppConst";
import { RcFile } from "antd/es/upload";

// export const apiGetProvider = async (providerId: string) => {
//   const url = `/payment-hub-provider/${providerId}`;
//   return API.get<Provider>(url);
// };

export const apiCreateTransactionLog = async (
  data: Pick<TransactionTypeLog, "transactionId" | "comment" | "type"> &
    Partial<TransactionTypeLog>,
) => {
  return API.post<TransactionTypeLog>(
    `/payment-hub-transaction/insert-log`,
    data,
  );
};

export const apiCreateTransaction = async (data: Partial<TransactionType>) => {
  return API.post<TransactionType>(`/payment-hub-transaction`, data);
};

export const apiUpdateTransaction = async (data: Partial<TransactionType>) => {
  const { id, ...rest } = data;
  return API.patch<TransactionType>(`/payment-hub-transaction/${id}`, rest);
};

export const apiGetTransactionFiles = async <T extends ApiParams>(
  params: T,
) => {
  const url = ApiUrlPrepare({ params, url: `/attachment-hub` });
  return API.get<PaginationType<TransactionAttachment>>(url);
};

export const apiUploadTransactionFile = async (
  id: string,
  file: RcFile,
  onProgress?: (progress: number) => void,
) => {
  return new Promise<TransactionType>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const formData = new FormData();
    formData.append("file", file);

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded * 100) / event.total);
        onProgress(progress);
      }
    });

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText);
          resolve(response);
        } catch (error) {
          reject(new Error("Invalid JSON response"));
        }
      } else {
        const response = JSON.parse(xhr.responseText);
        reject(new Error(response?.message || "Upload failed"));
        // reject(new Error(`Upload failed with status: ${xhr.status}`));
      }
    });

    xhr.addEventListener("error", () => {
      reject(new Error("Upload failed"));
    });

    xhr.open("PATCH", BE_BASE_URL + `/payment-hub-transaction/${id}`, true);
    xhr.setRequestHeader("Accept", "application/json");
    xhr.setRequestHeader(
      "Authorization",
      `Bearer ${redux.getState().auth?.user?.accessToken}`,
    );
    xhr.send(formData);
  });
};

export const apiDeleteTransactionFile = async (id: string) => {
  return API.delete<null>(`/attachment-hub/${id}`);
};

// /documents/download/:id/:fileName
export const apiOpenTransactionFile = async (data: TransactionAttachment) => {
  const url = (
    data.url || `${BE_BASE_URL}/documents/download/${data.id}`
  ).replace("http:", "https:");
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${data.token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`File download failed with status: ${response.status}`);
  }

  return response.blob();
};

export const apiGetTransaction = async (id: string) => {
  const url = ApiUrlPrepare({
    params: {
      id,
      join: [
        "customer",
        "provider",
        "providerTransactionConfig",
        "logs",
        "logs.manager",
        "attachments",
      ],
    },
    url: `/payment-hub-transaction`,
  });
  return API.get<TransactionType>(url);
};

export const apiGetTransactions = async <T extends ApiParams>(params: T) => {
  const url = ApiUrlPrepare({ params, url: `/payment-hub-transaction` });
  return API.get<PaginationType<TransactionType>>(url);
};

export const apiAddManualTransaction = async (
  data: Partial<ManualTransaction>,
) => {
  return API.post<ManualTransaction>(`/customer-manual-transaction`, data);
};

export const apiDeleteManualTransaction = async (data: ManualTransaction) => {
  return API.delete<null>(`/customer-manual-transaction/${data.id}`);
};

export const apiUpdateManualTransaction = async (
  data: Partial<ManualTransaction>,
) => {
  const { id, ...rest } = data;
  return API.patch<ManualTransaction>(
    `/customer-manual-transaction/${id}`,
    rest,
  );
};

export const apiGetManualTransactions = async <T extends ApiParams>(
  params: T,
) => {
  const url = ApiUrlPrepare({ params, url: `/customer-manual-transaction` });
  return API.get<PaginationType<ManualTransaction>>(url);
};
